import { Vector3 } from 'three'
import type { LabelOrientation } from '@api/fastAPI.schemas'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { ElementLabelDescriptor } from 'src/components/scene/ModelElementLabels/types'
import { LabelPlanExtent } from 'src/components/scene/ModelElementLabelsV2/utils'
import type { PlanLabelPositionOverride } from './usePlanLabelPositionOverrides'

/**
 * A plan label's orientation as the PDF export knows it: vertical labels are
 * the horizontal box turned by 90° and read bottom-up.
 */
export const getLabelOrientation = (descriptor: ElementLabelDescriptor): LabelOrientation =>
  Math.abs(descriptor.xDirection.y) > Math.abs(descriptor.xDirection.x) ? 'Vertical' : 'Horizontal'

export const toggleLabelOrientation = (orientation: LabelOrientation): LabelOrientation =>
  orientation === 'Horizontal' ? 'Vertical' : 'Horizontal'

/** below this plan distance a label counts as centered on its element */
const CENTERED_LABEL_TOLERANCE = 1e-6

/**
 * Where a label's center goes when it is turned by 90°: the box edge facing
 * its element stays put, so a label placed beside its element (walls,
 * columns, rips, lintels) keeps clearing it instead of growing into it. The
 * side is the page axis along which the label sits off the element's center;
 * a label centered on its element (beams, purlins, most slabs) turns in place.
 *
 * `extent` is the label box in its current orientation, relative to `center`.
 */
export const getRotatedLabelCenter = (
  center: { x: number; y: number },
  elementCenter: { x: number; y: number },
  extent: LabelPlanExtent,
): { x: number; y: number } => {
  const dx = center.x - elementCenter.x
  const dy = center.y - elementCenter.y
  if (Math.max(Math.abs(dx), Math.abs(dy)) < CENTERED_LABEL_TOLERANCE) {
    return { x: center.x, y: center.y }
  }
  // Turning by 90° swaps the box's half extents along x and y.
  const halfX = (extent.xMax - extent.xMin) / 2
  const halfY = (extent.yMax - extent.yMin) / 2
  if (Math.abs(dx) > Math.abs(dy)) {
    return { x: center.x + Math.sign(dx) * (halfY - halfX), y: center.y }
  }
  return { x: center.x, y: center.y + Math.sign(dy) * (halfX - halfY) }
}

/**
 * The descriptor with a persisted (user-moved / user-rotated) plan label
 * override applied: centered on the stored point and, when rotated, turned to
 * the stored orientation, like the PDF export's Center-anchored custom labels.
 */
export const applyPlanLabelOverride = (
  descriptor: ElementLabelDescriptor,
  override: PlanLabelPositionOverride,
  z: number = descriptor.position.z,
): ElementLabelDescriptor => {
  const position = new ImmutableVector3(override.x, override.y, z)
  if (!override.orientation || override.orientation === getLabelOrientation(descriptor)) {
    return { ...descriptor, position }
  }
  const isVertical = override.orientation === 'Vertical'
  return {
    ...descriptor,
    position,
    xDirection: isVertical ? new Vector3(0, 1, 0) : new Vector3(1, 0, 0),
    yDirection: isVertical ? new Vector3(-1, 0, 0) : new Vector3(0, 1, 0),
  }
}
